import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { auth, db } from "../firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import Loader from "../components/Loader";
import { useToast } from "../components/Toast";
import { useAppDialog } from "../components/AppDialog";
import { updateMyContact, nuevoRequestId, mensajeDeError, codigoDeError } from "../utils/identityApi";
import styles from "./Configuracion.module.css";
import {
  FaCog,
  FaBell,
  FaWhatsapp,
  FaEnvelope,
  FaPhone,
  FaCheck,
  FaWallet,
  FaCommentDots,
  FaExclamationCircle,
  FaBullhorn,
} from "react-icons/fa";
import { useIsMobile } from "../hooks/useResponsive";
import {
  AppScreen,
  AppHeader,
  AppCard,
  SectionLabel,
  IconBox,
} from "../components/app";
import { appColors, appType, appSpacing, appRadius } from "../utils/appTheme";
import { useIsReadOnly } from "../context/EffectiveIdentityContext";

// Los labels/hints se traducen en render vía t(): OPTIONS por `key`
// (options.<key>.label / .hint) y CHANNEL_OPTIONS por `value` (channels.<value>).
const OPTIONS = [
  {
    key: "gastoComunDisponible",
    icon: FaWallet,
    color: appColors.green,
  },
  {
    key: "respuestasIncidenciasPropias",
    icon: FaCommentDots,
    color: appColors.blue,
  },
  {
    key: "incidenciasEdificioRespondidas",
    icon: FaExclamationCircle,
    color: appColors.amber,
  },
  {
    key: "nuevasNovedadesEdificio",
    icon: FaBullhorn,
    color: appColors.blue,
  },
];

const CHANNEL_OPTIONS = [
  // "#25D366" es el brand-color oficial de WhatsApp (excepción documentada, no forma
  // parte de la paleta app pero identifica el canal). El resto sale de appTheme.
  { value: "whatsapp", icon: FaWhatsapp, color: "#25D366" },
  { value: "email", icon: FaEnvelope, color: appColors.blue },
  { value: "both", icon: FaBell, color: appColors.violet },
  { value: "none", icon: FaBell, color: appColors.ink3 },
];

/**
 * Mensaje para el usuario de un error de callable. Los del servidor ya vienen
 * en español; un error de red trae solo el código ("internal",
 * "deadline-exceeded"), y ahí va el texto genérico.
 */
const textoDeError = (err, fallback) => {
  const msg = mensajeDeError(err, fallback);
  return /^[a-z-]+$/.test(msg) ? fallback : msg;
};

const Configuracion = ({ setSelected, goBack }) => {
  const { t } = useTranslation("configuracion");
  const { t: tc } = useTranslation("common");
  const { showToast } = useToast();
  const { confirmDialog } = useAppDialog();
  const isMobile = useIsMobile();
  // "Ver como residente": modo solo lectura. Bloquea guardar preferencias.
  const readOnly = useIsReadOnly();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [phone, setPhone] = useState("");
  const [channel, setChannel] = useState("whatsapp");
  const [phoneSaving, setPhoneSaving] = useState(false);
  const [channelSaving, setChannelSaving] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [prefs, setPrefs] = useState({
    gastoComunDisponible: false,
    respuestasIncidenciasPropias: false,
    incidenciasEdificioRespondidas: false,
    nuevasNovedadesEdificio: false,
  });
  // Teléfono y canal tal como están guardados en notifications/{uid}
  // (channel null: nunca se eligió y la pantalla muestra WhatsApp).
  const guardadoRef = useRef({ phone: "", channel: null });
  // Teléfono y canal se guardan de a uno (updateMyContact): dos a la vez sobre
  // el mismo doc se pisan y el segundo vuelve con datos_cambiaron. Pasa al salir
  // del campo del número tocando un botón de canal.
  const colaRef = useRef(Promise.resolve());

  const cargar = useCallback(async () => {
    const user = auth.currentUser;
    if (!user?.uid) {
      setLoading(false);
      return;
    }
    try {
      const ref = doc(db, "notifications", user.uid);
      const snap = await getDoc(ref);
      const d = snap.exists() ? snap.data() || {} : {};
      setPrefs({
        gastoComunDisponible: d.gastoComunDisponible ?? false,
        respuestasIncidenciasPropias: d.respuestasIncidenciasPropias ?? false,
        incidenciasEdificioRespondidas: d.incidenciasEdificioRespondidas ?? false,
        nuevasNovedadesEdificio: d.nuevasNovedadesEdificio ?? false,
      });
      const tel = d.phone ? String(d.phone) : "";
      setPhone(tel);
      setChannel(d.channel || "whatsapp");
      guardadoRef.current = { phone: tel, channel: d.channel || null };
    } catch (err) {
      console.error("Error al cargar notificaciones:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const enCola = (fn) => {
    const p = colaRef.current.then(() => fn());
    colaRef.current = p.catch(() => {});
    return p;
  };

  /**
   * Teléfono de avisos y canal van por el servidor (updateMyContact), que
   * además registra el consentimiento. "Teléfono compartido" pide confirmar y
   * reenvía lo mismo con confirmarCompartido; si los datos cambiaron mientras
   * se guardaba, se reintenta una vez (el servidor relee todo). Devuelve la
   * respuesta, o null si la persona no confirmó.
   */
  const guardarContacto = async (payload, requestId) => {
    let extra = {};
    let reintentado = false;
    for (;;) {
      try {
        return await updateMyContact({ ...payload, ...extra, ...(requestId ? { requestId } : {}) });
      } catch (err) {
        const codigo = codigoDeError(err);
        const motivo = err?.details?.motivo;
        if (codigo === "failed-precondition" && motivo === "telefono_compartido" && !extra.confirmarCompartido) {
          const ok = await confirmDialog(textoDeError(err, t("toast.saveError")), {
            title: t("dialog.sharedPhoneTitle", "¿Es un teléfono compartido?"),
            confirmLabel: t("dialog.sharedPhoneConfirm", "Sí, es compartido"),
            cancelLabel: tc("cancel"),
          });
          if (!ok) return null;
          extra = { ...extra, confirmarCompartido: true };
          continue;
        }
        if (codigo === "aborted" && motivo === "datos_cambiaron" && !reintentado) {
          reintentado = true;
          continue;
        }
        throw err;
      }
    }
  };

  // Los 4 interruptores siguen directo en notifications/{uid}: solo la clave,
  // user (el email del token) y updatedAt. Teléfono y canal, por el servidor.
  const handleToggle = async (key, value) => {
    if (readOnly) { showToast(t("toast.readOnly")); return; }
    const user = auth.currentUser;
    if (!user?.uid) return;
    setPrefs((p) => ({ ...p, [key]: value }));
    setSaving(key);
    try {
      const ref = doc(db, "notifications", user.uid);
      await setDoc(ref, { [key]: value, user: user.email, updatedAt: serverTimestamp() }, { merge: true });
    } catch (err) {
      console.error("Error al guardar:", err);
      setPrefs((p) => ({ ...p, [key]: !value }));
      showToast(t("toast.saveError"), "error");
    } finally {
      setSaving(null);
    }
  };

  const formatPhoneForDisplay = (value) => {
    // Limpiar todo excepto numeros y +
    const clean = value.replace(/[^\d+]/g, "");
    return clean;
  };

  const validatePhone = (value) => {
    if (!value) return "";
    const clean = value.replace(/[^\d+]/g, "");
    // Debe empezar con + y tener entre 10-15 digitos
    if (!clean.startsWith("+")) return t("validation.countryCode");
    const digits = clean.replace("+", "");
    if (digits.length < 10 || digits.length > 15) return t("validation.length");
    return "";
  };

  // Al salir del campo: guarda el número solo si cambió ("" lo borra).
  const handleSavePhone = () => {
    if (readOnly) return;
    const user = auth.currentUser;
    if (!user?.uid) return;

    const telefono = phone;
    const error = validatePhone(telefono);
    if (error && telefono) {
      setPhoneError(error);
      return;
    }
    const canalVisible = channel;

    enCola(async () => {
      const guardado = guardadoRef.current;
      if (telefono === guardado.phone) return;
      const payload = { telefonoAvisos: telefono };
      // Como antes, si el canal nunca se guardó, va el que se ve junto con el número.
      if (guardado.channel == null) payload.canal = canalVisible;

      setPhoneSaving(true);
      setPhoneError("");
      try {
        // Un requestId por intento, reusado si hay que confirmar o reintentar.
        const res = await guardarContacto(payload, nuevoRequestId());
        if (!res) {
          // No confirmó que es compartido: vuelve el número guardado.
          setPhone((cur) => (cur === telefono ? guardadoRef.current.phone : cur));
          showToast(t("toast.phoneNotSaved", "No se guardó el número."), "info");
          return;
        }
        const perfil = res.perfil || {};
        const tel = perfil.telefonoAvisos !== undefined ? String(perfil.telefonoAvisos ?? "") : telefono;
        guardadoRef.current = {
          phone: tel,
          channel: perfil.canal !== undefined ? perfil.canal : guardadoRef.current.channel,
        };
        // El servidor lo devuelve normalizado (+598…), salvo que se siga escribiendo.
        setPhone((cur) => (cur === telefono ? tel : cur));
      } catch (err) {
        console.error("Error al guardar teléfono:", err);
        const codigo = codigoDeError(err);
        const msg = textoDeError(err, t("toast.saveError"));
        if (codigo === "invalid-argument") {
          setPhoneError(msg);
          return;
        }
        if (codigo === "aborted" && err?.details?.motivo === "datos_cambiaron") {
          showToast(msg, "warning");
          await cargar();
          return;
        }
        setPhone((cur) => (cur === telefono ? guardadoRef.current.phone : cur));
        showToast(msg, "error");
      } finally {
        setPhoneSaving(false);
      }
    });
  };

  const handleChannelChange = (newChannel) => {
    if (readOnly) { showToast(t("toast.readOnly")); return; }
    const user = auth.currentUser;
    if (!user?.uid) return;
    if (channelSaving) return;
    setChannel(newChannel);
    // Ya es el guardado: nada que mandar.
    if (newChannel === guardadoRef.current.channel) return;

    setChannelSaving(true);
    enCola(async () => {
      try {
        const res = await guardarContacto({ canal: newChannel });
        const perfil = (res && res.perfil) || {};
        guardadoRef.current = {
          ...guardadoRef.current,
          channel: perfil.canal !== undefined ? perfil.canal : newChannel,
        };
      } catch (err) {
        console.error("Error al guardar canal:", err);
        setChannel(guardadoRef.current.channel || "whatsapp"); // revert
        const msg = textoDeError(err, t("toast.saveError"));
        if (codigoDeError(err) === "aborted" && err?.details?.motivo === "datos_cambiaron") {
          showToast(msg, "warning");
          await cargar();
          return;
        }
        showToast(msg, "error");
      } finally {
        setChannelSaving(false);
      }
    });
  };

  if (loading) return <Loader />;

  // ─── Versión mobile: estilo app "Radial" ────────────────────────────────────
  if (isMobile) {
    const showPhone = channel === "whatsapp" || channel === "both";
    const activeChannel = CHANNEL_OPTIONS.find((o) => o.value === channel);
    const activeCount = OPTIONS.filter((o) => !!prefs[o.key]).length;
    return (
      <AppScreen
        header={
          <AppHeader
            title={t("title")}
            subtitle={t("subtitle")}
            onBack={goBack}
          />
        }
      >
        {/* Resumen de estado actual (info distinta al header: canal + avisos activos) */}
        <AppCard padding={16} style={{ marginBottom: appSpacing.section }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
            }}
          >
            <div
              style={{
                background: appColors.cream,
                borderRadius: appRadius.tile,
                padding: "12px 14px",
                minWidth: 0,
              }}
            >
              <div style={{ ...appType.statLabel, marginBottom: 5 }}>{t("summary.channel")}</div>
              <div style={{ ...appType.body, fontSize: 14, overflowWrap: "anywhere" }}>
                {activeChannel ? t(`channels.${activeChannel.value}`) : t("summary.channelUndefined")}
              </div>
            </div>
            <div
              style={{
                background: appColors.cream,
                borderRadius: appRadius.tile,
                padding: "12px 14px",
                minWidth: 0,
              }}
            >
              <div style={{ ...appType.statLabel, marginBottom: 5 }}>{t("summary.activeAlerts")}</div>
              <div style={{ ...appType.body, fontSize: 14, fontVariantNumeric: "tabular-nums" }}>
                {t("summary.activeCount", { count: activeCount, total: OPTIONS.length })}
              </div>
            </div>
          </div>
        </AppCard>

        {/* Canal de notificaciones */}
        <SectionLabel style={{ marginBottom: 12 }}>{t("channelSection.title")}</SectionLabel>
        <AppCard style={{ marginBottom: appSpacing.section }}>
          <p style={{ ...appType.bodySm, margin: "0 0 14px" }}>
            {t("channelSection.help")}
          </p>

          {/* Selector de canal (grilla que envuelve, sin scroll horizontal) */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: appSpacing.sm,
              marginBottom: showPhone ? 18 : 0,
            }}
          >
            {CHANNEL_OPTIONS.map((opt) => {
              const isActive = channel === opt.value;
              const Icon = opt.icon;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleChannelChange(opt.value)}
                  aria-pressed={isActive}
                  aria-busy={channelSaving && isActive ? true : undefined}
                  disabled={channelSaving}
                  style={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    minWidth: 0,
                    padding: "11px 12px",
                    borderRadius: 14,
                    border: `1px solid ${isActive ? appColors.blue : appColors.line}`,
                    background: isActive ? appColors.surface2 : appColors.surface,
                    color: isActive ? appColors.ink : appColors.ink2,
                    fontFamily: appType.fontFamily,
                    fontWeight: 400,
                    fontSize: 13.5,
                    cursor: channelSaving ? "progress" : "pointer",
                    opacity: channelSaving && !isActive ? 0.6 : 1,
                    transition: "border-color .15s ease, background .15s ease",
                  }}
                >
                  <IconBox
                    icon={<Icon size={15} />}
                    size={32}
                    bg={isActive ? appColors.surface : appColors.surface2}
                    color={isActive ? opt.color : appColors.ink3}
                  />
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}>
                    {t(`channels.${opt.value}`)}
                  </span>
                  {/* Radio de selección estilo app */}
                  <span
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      flexShrink: 0,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: `1.5px solid ${isActive ? appColors.blue : appColors.line}`,
                      background: isActive ? appColors.blue : "transparent",
                      transition: "background .15s ease, border-color .15s ease",
                    }}
                  >
                    {isActive && <FaCheck size={9} color={appColors.onBlue} />}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Campo de teléfono (visible si canal incluye WhatsApp) */}
          {showPhone && (
            <div>
              <label style={{ ...appType.bodySm, display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                <FaPhone size={11} />
                {t("phone.label")}
              </label>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => {
                      setPhone(formatPhoneForDisplay(e.target.value));
                      setPhoneError("");
                    }}
                    onBlur={handleSavePhone}
                    placeholder={t("phone.placeholder")}
                    style={{
                      width: "100%",
                      padding: "12px 14px",
                      borderRadius: 12,
                      border: `1px solid ${phoneError ? appColors.red : appColors.line}`,
                      fontSize: 14.5,
                      fontFamily: appType.fontFamily,
                      color: appColors.ink,
                      background: appColors.surface,
                      outline: "none",
                      transition: "border-color .15s",
                      boxSizing: "border-box",
                    }}
                    onFocus={(e) => { e.target.style.borderColor = appColors.blue; }}
                  />
                  {phoneError && (
                    <p style={{ ...appType.caption, color: appColors.red, margin: "6px 0 0" }}>{phoneError}</p>
                  )}
                  <p style={{ ...appType.caption, margin: "6px 0 0" }}>
                    {t("phone.hint")}
                  </p>
                </div>
                {phoneSaving && (
                  <div style={{
                    width: 20, height: 20, border: `2px solid ${appColors.line}`,
                    borderTop: `2px solid ${appColors.blue}`, borderRadius: "50%",
                    animation: "spin 1s linear infinite", flexShrink: 0, marginTop: 13,
                  }} />
                )}
              </div>
            </div>
          )}
        </AppCard>

        {/* Preferencias de notificación */}
        <SectionLabel style={{ marginBottom: 12 }}>{t("notificationsSection")}</SectionLabel>
        <AppCard padding={0}>
          {OPTIONS.map((opt, i) => {
            const Icon = opt.icon || FaBell;
            const isOn = !!prefs[opt.key];
            return (
              <div
                key={opt.key}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "15px 16px",
                  borderTop: i > 0 ? `1px solid ${appColors.line2}` : "none",
                }}
              >
                <IconBox
                  icon={<Icon size={15} />}
                  size={38}
                  color={isOn ? opt.color : appColors.ink3}
                />
                <label
                  htmlFor={`notif-${opt.key}`}
                  style={{ flex: 1, minWidth: 0, cursor: "pointer", display: "flex", flexDirection: "column", gap: 3 }}
                >
                  <span style={{ ...appType.body, fontSize: 13.5, overflowWrap: "anywhere" }}>
                    {t(`options.${opt.key}.label`)}
                  </span>
                  <span style={{ ...appType.caption, overflowWrap: "anywhere" }}>{t(`options.${opt.key}.hint`)}</span>
                </label>
                <label className={styles.switch} style={{ flexShrink: 0 }}>
                  <input
                    id={`notif-${opt.key}`}
                    type="checkbox"
                    checked={isOn}
                    onChange={(e) => handleToggle(opt.key, e.target.checked)}
                    disabled={saving === opt.key || readOnly}
                  />
                  <span className={styles.slider} />
                </label>
              </div>
            );
          })}
        </AppCard>
      </AppScreen>
    );
  }

  // ─── Versión desktop (existente) ─────────────────────────────────────────────
  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FaCog />
        </div>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>{t("title")}</h1>
          <p className={styles.subtitle}>{t("subtitleDesktop")}</p>
        </div>
      </div>

      {/* Canal de notificaciones */}
      <div className={styles.card} style={{ marginBottom: 16 }}>
        <div className={styles.sectionHeader}>
          <FaWhatsapp className={styles.sectionIcon} style={{ color: "#25D366" }} />
          <span>{t("channelSection.title")}</span>
        </div>
        <div style={{ padding: "16px 24px" }}>
          <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 16px 0" }}>
            {t("channelSection.help")}
          </p>

          {/* Selector de canal */}
          <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
            {CHANNEL_OPTIONS.map((opt) => {
              const isActive = channel === opt.value;
              const Icon = opt.icon;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleChannelChange(opt.value)}
                  aria-pressed={isActive}
                  aria-busy={channelSaving && isActive ? true : undefined}
                  disabled={channelSaving}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "10px 16px",
                    borderRadius: 10,
                    border: isActive ? `2px solid ${opt.color}` : "2px solid #e2e8f0",
                    background: isActive ? `${opt.color}10` : "#fff",
                    color: isActive ? opt.color : "#64748b",
                    fontWeight: 400,
                    fontSize: 13,
                    cursor: channelSaving ? "progress" : "pointer",
                    opacity: channelSaving && !isActive ? 0.6 : 1,
                    transition: "all 0.15s ease",
                    fontFamily: "inherit",
                  }}
                >
                  <Icon size={16} />
                  {t(`channels.${opt.value}`)}
                </button>
              );
            })}
          </div>

          {/* Campo de teléfono (visible si canal incluye WhatsApp) */}
          {(channel === "whatsapp" || channel === "both") && (
            <div style={{ marginBottom: 4 }}>
              <label style={{ fontSize: 12, fontWeight: 400, color: "#334155", display: "block", marginBottom: 6 }}>
                <FaPhone size={11} style={{ marginRight: 6 }} />
                {t("phone.label")}
              </label>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                <div style={{ flex: 1 }}>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => {
                      setPhone(formatPhoneForDisplay(e.target.value));
                      setPhoneError("");
                    }}
                    onBlur={handleSavePhone}
                    placeholder={t("phone.placeholder")}
                    style={{
                      width: "100%",
                      padding: "10px 14px",
                      borderRadius: 8,
                      border: phoneError ? "1.5px solid #ef4444" : "1.5px solid #e2e8f0",
                      fontSize: 14,
                      fontFamily: "inherit",
                      outline: "none",
                      transition: "border-color 0.15s",
                      boxSizing: "border-box",
                    }}
                    onFocus={(e) => { e.target.style.borderColor = "#0080ff"; }}
                  />
                  {phoneError && (
                    <p style={{ fontSize: 11, color: "#ef4444", margin: "4px 0 0 0" }}>{phoneError}</p>
                  )}
                  <p style={{ fontSize: 11, color: "#94a3b8", margin: "4px 0 0 0" }}>
                    {t("phone.hint")}
                  </p>
                </div>
                {phoneSaving && (
                  <div style={{
                    width: 20, height: 20, border: "2px solid #e5e7eb",
                    borderTop: "2px solid #0080ff", borderRadius: "50%",
                    animation: "spin 1s linear infinite", flexShrink: 0, marginTop: 10,
                  }} />
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Preferencias de notificación */}
      <div className={styles.card}>
        <div className={styles.sectionHeader}>
          <FaBell className={styles.sectionIcon} />
          <span>{t("notificationsSection")}</span>
        </div>
        <div className={styles.optionsList}>
          {OPTIONS.map((opt) => (
            <div key={opt.key} className={styles.optionRow}>
              <label className={styles.optionLabel}>{t(`options.${opt.key}.label`)}</label>
              <label className={styles.switch}>
                <input
                  type="checkbox"
                  checked={!!prefs[opt.key]}
                  onChange={(e) => handleToggle(opt.key, e.target.checked)}
                  disabled={saving === opt.key || readOnly}
                />
                <span className={styles.slider} />
              </label>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Configuracion;
